import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  TicketTimelineEvent,
  TimelineEventType
} from 'src/interfaces/TicketTypes';

const eventTypeKeys: Record<number, { id: string; defaultMessage: string }> = {
  [TimelineEventType.Created]:              { id: 'timelineCreated',         defaultMessage: 'Created' },
  [TimelineEventType.StatusChanged]:        { id: 'timelineStatusChanged',   defaultMessage: 'Status Changed' },
  [TimelineEventType.Assigned]:             { id: 'timelineAssigned',        defaultMessage: 'Assigned' },
  [TimelineEventType.CommentAdded]:         { id: 'timelineCommentAdded',    defaultMessage: 'Comment Added' },
  [TimelineEventType.AiDiagnosisAttached]:  { id: 'timelineAiDiagnosis',     defaultMessage: 'AI Diagnosis' },
  [TimelineEventType.Escalated]:            { id: 'timelineEscalated',       defaultMessage: 'Escalated' },
  [TimelineEventType.ResolutionAdded]:      { id: 'timelineResolutionAdded', defaultMessage: 'Resolution Added' }
};

const eventTypeColors: Record<number, string> = {
  [TimelineEventType.Created]: '#1976d2',
  [TimelineEventType.StatusChanged]: '#ed6c02',
  [TimelineEventType.Assigned]: '#9c27b0',
  [TimelineEventType.CommentAdded]: '#2e7d32',
  [TimelineEventType.AiDiagnosisAttached]: '#0288d1',
  [TimelineEventType.Escalated]: '#d32f2f',
  [TimelineEventType.ResolutionAdded]: '#4caf50'
};

interface TimelinePanelProps {
  events: TicketTimelineEvent[];
}

function TimelinePanel({ events }: TimelinePanelProps) {
  const intl = useIntl();
  const sorted = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  return (
    <Card>
      <CardHeader
        title={
          <FormattedMessage id="timeline" defaultMessage="Timeline" />
        }
      />
      <Divider />
      <CardContent>
        {sorted.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="noTimelineEvents"
              defaultMessage="No events yet."
            />
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {sorted.map((event) => (
              <Box
                key={event.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      eventTypeColors[event.eventType] ?? '#757575',
                    mt: 0.8,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {eventTypeKeys[event.eventType]
                      ? intl.formatMessage(eventTypeKeys[event.eventType])
                      : 'Event'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {event.description}
                  </Typography>
                  <Typography variant="caption" color="text.disabled">
                    {new Date(event.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default TimelinePanel;